import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Paper,
  Button,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import swal from 'sweetalert';
import config from '../../Config';

const ResponsiveSelect = styled(Select)(({ theme }) => ({
  minWidth: '100%',
  '& .MuiSelect-select': {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export default function AddMappingView({ propertyId, onClose, onSuccess }) {
  const [categories, setCategories] = useState([]);
  const [subCatMap, setSubCatMap] = useState({});
  const [productMap, setProductMap] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedCategoryHasSub, setSelectedCategoryHasSub] = useState(false);

  const [productDetails, setProductDetails] = useState({
    name: '',
    description: '',
    image: '',
  });

  const [loadingProduct, setLoadingProduct] = useState(false);
  const [rowSaving, setRowSaving] = useState(false);

  const [price, setPrice] = useState('');
  const [priceType, setPriceType] = useState('fixed');

  useEffect(() => {
    fetchAllCategories();
  }, []);

  const fetchAllCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/category`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCategories(res.ok ? data.categories : []);
    } catch {
      setCategories([]);
    }
  };

  const loadSubCategories = async (catId) => {
    if (subCatMap[catId]) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${config.apiBaseUrl}/admin/upsale/subCategory-by-category/${catId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setSubCatMap((prev) => ({
        ...prev,
        [catId]: res.ok ? data.subCategories : [],
      }));
    } catch {}
  };

  const loadProducts = async (categoryId, subCatId = null) => {
    const key = subCatId || categoryId;
    if (productMap[key]) return;
    try {
      const token = localStorage.getItem('token');
      const query = subCatId ? `subCategory=${subCatId}` : `category=${categoryId}`;
      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/product?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setProductMap((prev) => ({
        ...prev,
        [key]: res.ok ? data.products : [],
      }));
    } catch {}
  };

  const fetchProductDetails = async (productId) => {
    if (!productId) {
      setProductDetails({ name: '', description: '', image: '' });
      return;
    }

    setLoadingProduct(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/product/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.success && data.product) {
        setProductDetails({
          name: data.product.name || '',
          description: data.product.description || '',
          image: data.product.image || '',
        });
      }
    } catch {
      setProductDetails({ name: '', description: '', image: '' });
    } finally {
      setLoadingProduct(false);
    }
  };

  const handleSaveMapping = async () => {
    if (!selectedCategory || !selectedProduct) {
      return swal('Warning', 'Select category and product', 'warning');
    }

    if (!price || Number(price) <= 0) {
      return swal('Warning', 'Enter valid price', 'warning');
    }

    setRowSaving(true);
    try {
      const token = localStorage.getItem('token');

      const body = {
        propertyId,
        productId: selectedProduct,
        category: selectedCategory,
        subCategory: selectedCategoryHasSub ? selectedSubCategory : null,
        taxPercent: 0,
        price: Number(price),
        priceType: priceType,
      };

      const resp = await fetch(`${config.apiBaseUrl}/admin/upsale/property/add-product`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await resp.json();
      if (!data.success) throw new Error(data.message);

      swal('Success', 'Mapping saved!', 'success');
      onSuccess();
    } catch (err) {
      swal('Error', err.message, 'error');
    } finally {
      setRowSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', gap: 4, width: '100%', height: '100%' }}>
      {/* Left Panel */}
      <Box sx={{ flex: 1, minWidth: 300 }}>
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={onClose}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">Select Product</Typography>
          </Box>

          <Stack spacing={2}>
            <ResponsiveSelect
              value={selectedCategory}
              displayEmpty
              onChange={(e) => {
                const catId = e.target.value;
                setSelectedCategory(catId);
                const cat = categories.find((c) => c._id === catId);
                setSelectedCategoryHasSub(cat?.hasSubCategory || false);
                setSelectedSubCategory('');
                setSelectedProduct('');
                setProductDetails({ name: '', description: '', image: '' });
                loadSubCategories(catId);
                loadProducts(catId);
              }}
            >
              <MenuItem value="">Select Category</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}
                </MenuItem>
              ))}
            </ResponsiveSelect>

            {selectedCategoryHasSub && (
              <ResponsiveSelect
                value={selectedSubCategory}
                displayEmpty
                onChange={(e) => {
                  setSelectedSubCategory(e.target.value);
                  setSelectedProduct('');
                  setProductDetails({ name: '', description: '', image: '' });
                  loadProducts(selectedCategory, e.target.value);
                }}
              >
                <MenuItem value="">Select Subcategory</MenuItem>
                {(subCatMap[selectedCategory] || []).map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.name}
                  </MenuItem>
                ))}
              </ResponsiveSelect>
            )}

            <ResponsiveSelect
              value={selectedProduct}
              displayEmpty
              onChange={(e) => {
                setSelectedProduct(e.target.value);
                fetchProductDetails(e.target.value);
              }}
            >
              <MenuItem value="">Select Product</MenuItem>
              {(productMap[selectedSubCategory || selectedCategory] || []).map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.name}
                </MenuItem>
              ))}
            </ResponsiveSelect>

            <Stack direction="row" spacing={2}>
              <TextField
                label="Price"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                fullWidth
                helperText={priceType === 'hourly' ? 'Hourly price' : 'Fixed price'}
              />

              <TextField
                select
                label="Rate"
                value={priceType}
                onChange={(e) => setPriceType(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="fixed">Fixed</MenuItem>
                <MenuItem value="hourly">Per Hour</MenuItem>
              </TextField>
            </Stack>

            <Button
              variant="contained"
              onClick={handleSaveMapping}
              disabled={!selectedProduct || rowSaving}
            >
              {rowSaving ? <CircularProgress size={20} /> : 'Add Mapping'}
            </Button>
          </Stack>
        </Paper>
      </Box>

      {/* Right Panel */}
      <Box sx={{ flex: 2 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Product Details
          </Typography>

          {loadingProduct ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <TextField label="Name" value={productDetails.name} disabled fullWidth />
              <TextField
                label="Description"
                value={productDetails.description}
                disabled
                multiline
                rows={6}
                fullWidth
              />

              <Box
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 250,
                  bgcolor: '#f0f0f0',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  mx: 'auto',
                }}
              >
                {productDetails.image ? (
                  <img
                    src={productDetails.image}
                    alt={productDetails.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No Image
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
